import { useState, useEffect, useRef } from 'react';
import {
  buildStyleIdFilter,
  mapBeerSearchHits,
  resolveBeerSearchClient,
} from './beerIndexSearchHelpers';

/**
 * useBeerIndex
 * Beer search via Typesense (browser search API key). Browse (`*`) is sorted by name;
 * text search uses relevance. Highlights on `name` are mapped for BeerCard.
 *
 * @param {string} searchQuery - Query string; empty/`*` treated as browse
 * @param {Object} options
 * @param {number} [options.pageSize=50] - Results per page (max 100)
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.refreshKey=0] - Increment to force refetch with same query.
 * @param {string[]|null} [options.styleIds=null] - When set, filter beers by style ids.
 * @param {boolean} [options.enabled=true] - Skip search when false.
 * @returns {{beers: object[], loading: boolean, error: string|null, found: number}}
 */
export function useBeerIndex(searchQuery = '*', options = {}) {
  const {
    pageSize = 50,
    page = 1,
    refreshKey = 0,
    styleIds = null,
    enabled = true,
  } = options;
  const [beers, setBeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [found, setFound] = useState(0);
  const isMountedRef = useRef(true);
  const styleIdsKey = styleIds === null
    ? 'none'
    : `ids:${(Array.isArray(styleIds) ? styleIds : []).join('|')}`;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const activeStyleIds = styleIdsKey === 'none'
      ? null
      : styleIdsKey.slice(4).split('|').filter((styleId) => styleId.length > 0);

    const applyIdleState = () => {
      setBeers([]);
      setFound(0);
      setLoading(false);
      setError(null);
    };

    const performSearch = async () => {
      if (!isMountedRef.current) return;

      if (!enabled) {
        applyIdleState();
        return;
      }

      if (Array.isArray(activeStyleIds) && activeStyleIds.length === 0) {
        applyIdleState();
        return;
      }

      setLoading(true);
      setError(null);

      const resolved = resolveBeerSearchClient();
      if (resolved.error) {
        if (!cancelled && isMountedRef.current) {
          setBeers([]);
          setFound(0);
          setError(resolved.error);
          setLoading(false);
        }
        return;
      }

      const perPage = Math.min(100, Math.max(1, pageSize));
      const pageNum = Math.max(1, page);
      const query = searchQuery || '*';
      const isBrowse = !searchQuery || searchQuery === '*';

      const searchParams = {
        q: query,
        query_by: 'name,breweryName',
        per_page: perPage,
        page: pageNum,
        highlight_full_fields: 'name',
      };
      if (isBrowse) {
        searchParams.sort_by = 'name:asc';
      }
      const styleFilter = buildStyleIdFilter(activeStyleIds);
      if (styleFilter) {
        searchParams.filter_by = styleFilter;
      } else if (Array.isArray(activeStyleIds)) {
        applyIdleState();
        return;
      }

      try {
        const searchResponse = await resolved.client
          .collections(resolved.collectionName)
          .documents()
          .search(searchParams);

        if (cancelled || !isMountedRef.current) return;

        setBeers(mapBeerSearchHits(searchResponse?.hits));
        setFound(Number(searchResponse?.found) || 0);
      } catch (err) {
        if (cancelled || !isMountedRef.current) return;
        console.error('Error searching beers:', err);
        const errorMessage =
          err?.message || 'Failed to load beers. Please try again.';
        setError(errorMessage);
        setBeers([]);
        setFound(0);
      } finally {
        if (!cancelled && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    performSearch();

    return () => {
      cancelled = true;
    };
  }, [searchQuery, page, pageSize, refreshKey, styleIdsKey, enabled]);

  return { beers, loading, error, found };
}

export default useBeerIndex;
